import { useId, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { Alert } from '../atoms/Alert/Alert';
import { Badge } from '../atoms/Badge/Badge';
import { Button } from '../atoms/Button/Button';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Text } from '../atoms/Typography/Text';
import { Textarea } from '../atoms/Textarea/Textarea';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { ApiError } from '../../lib/apiClient';
import type { SocialComment } from '../../services/communityService';
import { useTranslation } from '../../i18n/useTranslation';
import './CommentThread.css';

/** Server-side limit of the image comment API (the journal one allows more). */
export const COMMENT_MAX_LENGTH = 2000;

export interface CommentThreadProps {
  queryKey: QueryKey;
  load: () => Promise<SocialComment[]>;
  create: (body: string) => Promise<SocialComment>;
  remove: (commentId: string) => Promise<void>;
  /** Invalidated after a change (e.g. the entry, for its comment count). */
  relatedQueryKeys?: QueryKey[];
  testId?: string;
}

function formatDateTime(iso: string, language: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

/**
 * Comments of a journal entry or an image: list (with nested replies,
 * read-only), add, delete (own, or — for the owner of the entry/image — any).
 * Comments are plain text, rendered as escaped React text.
 */
export function CommentThread({ queryKey, load, create, remove, relatedQueryKeys = [], testId }: CommentThreadProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const fieldId = useId();
  const [draft, setDraft] = useState('');
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);
  const [actionError, setActionError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const { data: comments, isLoading, isError, refetch } = useQuery({ queryKey, queryFn: load });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey });
    for (const key of relatedQueryKeys) void queryClient.invalidateQueries({ queryKey: key });
  };

  const createMutation = useMutation({
    mutationFn: (body: string) => create(body),
    onSuccess: () => {
      setDraft('');
      setActionError(null);
      setAnnouncement(t('comments.posted'));
      invalidate();
    },
    onError: (err: unknown) => {
      setActionError(err instanceof ApiError && err.status === 429 ? t('comments.rateLimited') : t('comments.postFailed'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (commentId: string) => remove(commentId),
    onSuccess: () => {
      setActionError(null);
      setAnnouncement(t('comments.removed'));
      invalidate();
    },
    onError: () => setActionError(t('comments.deleteFailed')),
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const body = draft.trim();
    if (body.length === 0) {
      setFieldError(t('comments.required'));
      return;
    }
    if (body.length > COMMENT_MAX_LENGTH) {
      setFieldError(t('comments.tooLong', { max: COMMENT_MAX_LENGTH }));
      return;
    }
    setFieldError(undefined);
    createMutation.mutate(body);
  }

  function handleDelete(commentId: string): void {
    if (window.confirm(t('comments.deleteConfirm'))) deleteMutation.mutate(commentId);
  }

  function authorLabel(comment: SocialComment): string {
    if (comment.isOwn) return t('comments.you');
    if (comment.isEntryAuthor) return t('comments.author');
    return t('comments.member');
  }

  function renderComment(comment: SocialComment) {
    return (
      <li key={comment.id} className="rg-comment-thread__item" data-testid="comment-item">
        <div className="rg-comment-thread__meta">
          {comment.isDeleted ? (
            <Text as="span" size="sm" color="muted">{t('comments.deleted')}</Text>
          ) : (
            <Badge tone={comment.isOwn || comment.isEntryAuthor ? 'info' : 'neutral'}>{authorLabel(comment)}</Badge>
          )}
          <Text as="span" size="sm" color="muted">
            <time dateTime={comment.createdAt}>{formatDateTime(comment.createdAt, i18n.language)}</time>
          </Text>
        </div>
        {!comment.isDeleted && <Text className="rg-comment-thread__body">{comment.body}</Text>}
        {comment.canDelete && !comment.isDeleted && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="rg-comment-thread__delete"
            onClick={() => handleDelete(comment.id)}
            disabled={deleteMutation.isPending}
          >
            {t('comments.delete')}
          </Button>
        )}
        {comment.replies.length > 0 && (
          <ul className="rg-comment-thread__replies" aria-label={t('comments.replies')}>
            {comment.replies.map(renderComment)}
          </ul>
        )}
      </li>
    );
  }

  let list;
  if (isLoading) {
    list = <LiveRegion><Text color="muted">{t('comments.loading')}</Text></LiveRegion>;
  } else if (isError) {
    list = <LoadErrorState title={t('comments.loadError')} titleLevel={4} onRetry={() => void refetch()} compact />;
  } else if (!comments || comments.length === 0) {
    list = <Text color="muted" data-testid="comments-empty">{t('comments.empty')}</Text>;
  } else {
    list = <ul className="rg-comment-thread__list">{comments.map(renderComment)}</ul>;
  }

  return (
    <div className="rg-comment-thread" data-testid={testId}>
      {list}
      {actionError && (
        <Alert tone="danger" compact onDismiss={() => setActionError(null)} dismissLabel={t('comments.dismiss')}>
          {actionError}
        </Alert>
      )}
      <form className="rg-comment-thread__form" onSubmit={handleSubmit} noValidate>
        <Textarea
          id={`${fieldId}-comment`}
          label={t('comments.label')}
          placeholder={t('comments.placeholder')}
          helperText={t('comments.hint', { max: COMMENT_MAX_LENGTH })}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          maxLength={COMMENT_MAX_LENGTH}
          rows={3}
          fullWidth
          error={fieldError}
          disabled={createMutation.isPending}
        />
        <div className="rg-comment-thread__actions">
          <Button type="submit" variant="secondary" disabled={createMutation.isPending}>
            {createMutation.isPending ? t('comments.submitting') : t('comments.submit')}
          </Button>
        </div>
      </form>
      <LiveRegion visuallyHidden>{announcement}</LiveRegion>
    </div>
  );
}

export default CommentThread;
